// Registration drafts saved on this device (plan §6.4), so a half-typed registration
// survives a refresh or a dropped connection.
//
// A draft holds PHI on the device. It is keyed per user and facility (a facility belongs
// to one tenant), never sent anywhere, expires after DRAFT_TTL_MS, and every draft on the
// device is deleted on sign-out and on idle timeout (plan risk 7; recorded for the DPIA;
// ADR 0018). The same device store holds the retry queue (`queue:` keys), which does not
// expire on its own but is deleted on sign-out too.

export interface DraftStore {
  /** False when the browser offers no storage; the form then doesn't claim to save. */
  readonly available: boolean;
  get(key: string): Promise<unknown>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  keys(): Promise<string[]>;
}

/** How long a draft stays on this device: one long shift. Older drafts are deleted unread. */
export const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

/** Keys of the retry queue (ADR 0018); drafts use every other prefix. */
export const QUEUE_KEY_PREFIX = 'queue:';

const DB_NAME = 'wodi-drafts';
const STORE = 'drafts';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('IndexedDB request failed'));
    };
  });
}

function indexedDbStore(factory: IDBFactory): DraftStore {
  let db: Promise<IDBDatabase> | null = null;
  const open = () => {
    db ??= new Promise((resolve, reject) => {
      const req = factory.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => {
        resolve(req.result);
      };
      req.onerror = () => {
        reject(req.error ?? new Error('IndexedDB could not be opened'));
      };
    });
    return db;
  };
  const run = async <T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const database = await open();
    return request(fn(database.transaction(STORE, mode).objectStore(STORE)));
  };
  return {
    available: true,
    get: (key) => run('readonly', (s) => s.get(key) as IDBRequest<unknown>),
    set: async (key, value) => {
      await run('readwrite', (s) => s.put(value, key));
    },
    delete: async (key) => {
      await run('readwrite', (s) => s.delete(key));
    },
    clear: async () => {
      await run('readwrite', (s) => s.clear());
    },
    keys: async () => {
      const keys = await run('readonly', (s) => s.getAllKeys());
      return keys.filter((k): k is string => typeof k === 'string');
    },
  };
}

/** For tests, and as the shape of the IndexedDB store. */
export function memoryDraftStore(): DraftStore & { readonly entries: Map<string, unknown> } {
  const entries = new Map<string, unknown>();
  return {
    available: true,
    entries,
    get: (key) => Promise.resolve(structuredClone(entries.get(key))),
    set: (key, value) => {
      entries.set(key, structuredClone(value));
      return Promise.resolve();
    },
    delete: (key) => {
      entries.delete(key);
      return Promise.resolve();
    },
    clear: () => {
      entries.clear();
      return Promise.resolve();
    },
    keys: () => Promise.resolve([...entries.keys()]),
  };
}

const unavailable: DraftStore = {
  available: false,
  get: () => Promise.resolve(undefined),
  set: () => Promise.resolve(),
  delete: () => Promise.resolve(),
  clear: () => Promise.resolve(),
  keys: () => Promise.resolve([]),
};

interface Dated {
  wodiDraft: 1;
  savedAt: number;
  value: unknown;
}

function isDated(stored: unknown): stored is Dated {
  return (
    typeof stored === 'object' &&
    stored !== null &&
    (stored as Partial<Dated>).wodiDraft === 1 &&
    typeof (stored as Partial<Dated>).savedAt === 'number'
  );
}

function isFresh(stored: unknown, now: number): stored is Dated {
  return isDated(stored) && now - stored.savedAt <= DRAFT_TTL_MS && stored.savedAt <= now + 60_000;
}

/**
 * The draft view of the device store: each value is saved with its date, and a value past
 * DRAFT_TTL_MS (or saved before drafts carried a date) is deleted instead of returned.
 */
function expiring(inner: DraftStore): DraftStore {
  return {
    available: inner.available,
    get: async (key) => {
      const stored = await inner.get(key);
      if (stored === undefined || stored === null) return undefined;
      if (isFresh(stored, Date.now())) return stored.value;
      await inner.delete(key);
      return undefined;
    },
    set: (key, value) => {
      const dated: Dated = { wodiDraft: 1, savedAt: Date.now(), value };
      return inner.set(key, dated);
    },
    delete: (key) => inner.delete(key),
    clear: () => inner.clear(),
    keys: async () => (await inner.keys()).filter((k) => !k.startsWith(QUEUE_KEY_PREFIX)),
  };
}

let store: DraftStore = typeof indexedDB === 'undefined' ? unavailable : indexedDbStore(indexedDB);
let drafts: DraftStore = expiring(store);

/** Drafts on this device, dated and expiring. The same object until the store is swapped. */
export function draftStore(): DraftStore {
  return drafts;
}

/** The device store itself, without expiry: the retry queue lives here. */
export function deviceStore(): DraftStore {
  return store;
}

/** Tests swap in a memory store. */
export function setDraftStore(next: DraftStore): void {
  store = next;
  drafts = expiring(next);
}

/** Deletes drafts past their time (not the retry queue). Run when the app starts. */
export async function sweepExpiredDrafts(): Promise<void> {
  try {
    const now = Date.now();
    for (const key of await store.keys()) {
      if (key.startsWith(QUEUE_KEY_PREFIX)) continue;
      if (!isFresh(await store.get(key), now)) await store.delete(key);
    }
  } catch {
    // Storage blocked: nothing can be read either.
  }
}

export function registrationDraftKey(userId: string, facilityId: string): string {
  return `registration:${userId}:${facilityId}`;
}

/** A triage form in progress for one visit (triage plan §6.3); cleared like every draft. */
export function triageDraftKey(userId: string, facilityId: string, visitId: string): string {
  return `triage:${userId}:${facilityId}:${visitId}`;
}

/**
 * A consultation note being written (consultation plan P8): kept on the device while
 * offline and between autosaves; cleared on signing and like every draft.
 */
export function noteDraftKey(userId: string, facilityId: string, consultationId: string): string {
  return `note:${userId}:${facilityId}:${consultationId}`;
}

/**
 * Deletes every draft and queued action on this device. Called on sign-out and idle
 * timeout so the next person at a shared workstation can't see what was typed.
 */
export async function clearAllDrafts(): Promise<void> {
  try {
    await store.clear();
  } catch {
    // Nothing to clear, or storage is blocked; either way no draft is readable.
  }
}
